import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useApp } from '../../context/AppContext';
import { meses, anos } from '../../data/mockData';
import { isCorretorSemPontuacao } from '../../utils/corretorUtils';

function VendasMes({ ano: anoInicial, onMesChange, onAnoChange }) {
  const { corretores, atualizarVenda, atualizarVendasBatch, atualizarStatusAtivo } = useApp();
  const [anoSelecionado, setAnoSelecionado] = useState(anoInicial);
  const [mesSelecionado, setMesSelecionado] = useState('Janeiro');
  const [openDialog, setOpenDialog] = useState(false);
  const [corretorSelecionado, setCorretorSelecionado] = useState('');
  const [valoresTemporarios, setValoresTemporarios] = useState({});
  const [agenciamentosTemporarios, setAgenciamentosTemporarios] = useState({});
  const [numeroContratosTemporarios, setNumeroContratosTemporarios] = useState({});
  const [pontuacaoMetaTemporarios, setPontuacaoMetaTemporarios] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [temAlteracoesPendentes, setTemAlteracoesPendentes] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Update anoSelecionado when prop changes
  useEffect(() => {
    if (anoInicial) {
      setAnoSelecionado(anoInicial);
    }
  }, [anoInicial]);

  // Reset mes to Janeiro when ano changes
  useEffect(() => {
    setMesSelecionado('Janeiro');
  }, [anoSelecionado]);

  // Notify parent when mes changes
  useEffect(() => {
    if (onMesChange && mesSelecionado) {
      onMesChange(mesSelecionado);
    }
  }, [mesSelecionado, onMesChange]);

  // Notify parent when ano changes
  useEffect(() => {
    if (onAnoChange && anoSelecionado) {
      onAnoChange(anoSelecionado);
    }
  }, [anoSelecionado, onAnoChange]);

  const handleMesChange = (novoMes) => {
    setMesSelecionado(novoMes);
  };

  const handleAnoChange = (novoAno) => {
    setAnoSelecionado(novoAno);
  };

  // Format currency
  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(valor);
  };

  // Initialize all active corretores with vendas = 0 for this month on mount (optimized with batch)
  useEffect(() => {
    let isMounted = true;
    
    const inicializarCorretoresAtivos = async () => {
      if (corretores.length === 0 || !mesSelecionado || !anoSelecionado) return;
      
      const corretoresAtivos = corretores.filter(c => c.ativo);
      
      // Collect all corretores that need initialization
      const vendasParaInicializar = [];
      
      corretoresAtivos.forEach(corretor => {
        // Check if corretor already has a venda entry for this month
        const temVenda = corretor.vendas && corretor.vendas[anoSelecionado] && corretor.vendas[anoSelecionado][mesSelecionado] !== undefined;
        
        if (!temVenda) {
          vendasParaInicializar.push({
            corretorId: corretor.id,
            ano: anoSelecionado,
            mes: mesSelecionado,
            valor: 0,
            pontuacaoMeta: 0,
            agenciamento: 0,
            numeroContratos: 0,
          });
        }
      });
      
      // Batch initialize all vendas at once (reduces egress significantly)
      if (vendasParaInicializar.length > 0 && isMounted) {
        try {
          await atualizarVendasBatch(vendasParaInicializar);
        } catch (error) {
          console.error('Error batch initializing vendas:', error);
        }
      }
    };

    inicializarCorretoresAtivos();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesSelecionado, anoSelecionado, corretores.length]); // Only run when mesSelecionado, anoSelecionado, or corretores count changes

  // Função para formatar valor como moeda brasileira (R$)
  const formatarValorMoeda = (valor) => {
    if (valor === '' || valor === null || valor === undefined) return '';
    const numero = typeof valor === 'number' ? valor : parseFloat(valor) || 0;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numero);
  };

  // Get corretores ativos
  const corretoresAtivos = corretores.filter(c => c.ativo);
  
  // Helper function to get venda value (backward compatible)
  const getVendaValor = (corretor, ano, mes) => {
    const venda = corretor.vendas?.[ano]?.[mes];
    if (!venda) return 0;
    // Support both old format (number) and new format (object)
    return typeof venda === 'object' ? (venda.valor || 0) : venda;
  };

  // Helper function to get agenciamento value (integer)
  const getAgenciamento = (corretor, ano, mes) => {
    const venda = corretor.vendas?.[ano]?.[mes];
    if (!venda) return 0;
    // Support both old format (number) and new format (object)
    const agenciamento = typeof venda === 'object' ? (venda.agenciamento || 0) : 0;
    return parseInt(agenciamento, 10); // Ensure it's an integer
  };

  // Helper function to get numero_contratos value (integer)
  const getNumeroContratos = (corretor, ano, mes) => {
    const venda = corretor.vendas?.[ano]?.[mes];
    if (!venda) return 0;
    // Support both old format (number) and new format (object)
    const numeroContratos = typeof venda === 'object' ? (venda.numero_contratos || 0) : 0;
    return parseInt(numeroContratos, 10); // Ensure it's an integer
  };

  // Helper function to get pontuacao_meta value (integer)
  const getPontuacaoMeta = (corretor, ano, mes) => {
    const venda = corretor.vendas?.[ano]?.[mes];
    if (!venda || typeof venda !== 'object') return 0;
    const pontuacaoMeta = venda.pontuacao_meta ?? venda.pontuacaoMeta ?? 0;
    return parseInt(pontuacaoMeta, 10) || 0;
  };

  // Map all corretores with their vendas for this month
  const todosCorretoresComVendas = corretores.map(corretor => {
    const vgv = getVendaValor(corretor, anoSelecionado, mesSelecionado);
    const agenciamento = getAgenciamento(corretor, anoSelecionado, mesSelecionado);
    const numeroContratos = getNumeroContratos(corretor, anoSelecionado, mesSelecionado);
    const pontuacaoMeta = getPontuacaoMeta(corretor, anoSelecionado, mesSelecionado);
    // Check if corretor has a vendas entry for this month (even if 0)
    const temVenda = corretor.vendas && corretor.vendas[anoSelecionado] && corretor.vendas[anoSelecionado][mesSelecionado] !== undefined;
    return { ...corretor, vgv, agenciamento, numeroContratos, pontuacaoMeta, temVenda };
  });

  // Get corretores that have vendas entry for this month (vgv >= 0)
  const corretoresComVendas = todosCorretoresComVendas
    .filter(c => c.temVenda)
    .sort((a, b) => {
      // Sort by vgv descending
      return b.vgv - a.vgv;
    });

  // Calculate monthly totals for KPI cards (including Loja sales in total VGV)
  const totalVgvMes = corretoresComVendas
    .reduce((acc, c) => {
      const val = valoresTemporarios[c.id] !== undefined ? valoresTemporarios[c.id] : c.vgv;
      return acc + (val || 0);
    }, 0);

  const totalAgenciamentosMes = corretoresComVendas
    .reduce((acc, c) => {
      const val = agenciamentosTemporarios[c.id] !== undefined ? agenciamentosTemporarios[c.id] : c.agenciamento;
      return acc + (val || 0);
    }, 0);

  const totalContratosMes = corretoresComVendas
    .reduce((acc, c) => {
      const val = numeroContratosTemporarios[c.id] !== undefined ? numeroContratosTemporarios[c.id] : c.numeroContratos;
      return acc + (val || 0);
    }, 0);

  const corretoresComVendaEfetiva = corretoresComVendas
    .filter(c => {
      const val = valoresTemporarios[c.id] !== undefined ? valoresTemporarios[c.id] : c.vgv;
      return val > 0;
    }).length;

  // Get IDs of corretores that already have vendas entry this month
  const corretoresComVendaNoMes = todosCorretoresComVendas
    .filter(c => c.temVenda)
    .map(c => c.id);

  // Filter corretores ativos that are not yet in the list with vendas
  // This allows adding corretores that don't have vendas yet
  const corretoresDisponiveis = corretoresAtivos.filter(
    c => !corretoresComVendaNoMes.includes(c.id)
  );

  const handleAdicionarCorretor = async () => {
    if (corretorSelecionado) {
      try {
        // Initialize venda with 0 for the selected corretor
        await handleSalvarVenda(corretorSelecionado, 0, 0, 0, 0);
        setCorretorSelecionado('');
        setOpenDialog(false);
      } catch (error) {
        console.error('Error adding corretor to vendas:', error);
        setSnackbar({
          open: true,
          message: 'Erro ao adicionar corretor. Tente novamente.',
          severity: 'error'
        });
      }
    }
  };

  const handleSalvarVenda = async (
    corretorId,
    valorNumerico,
    agenciamentoNumerico = 0,
    numeroContratosNumerico = 0,
    pontuacaoMetaNumerico = 0
  ) => {
    try {
      await atualizarVenda(
        corretorId,
        anoSelecionado,
        mesSelecionado,
        valorNumerico,
        agenciamentoNumerico,
        numeroContratosNumerico,
        pontuacaoMetaNumerico
      );
      // Success - value is saved automatically
      console.log('Venda salva automaticamente:', {
        corretorId,
        valorNumerico,
        agenciamentoNumerico,
        numeroContratosNumerico,
        pontuacaoMetaNumerico
      });
    } catch (error) {
      console.error('Error saving venda:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar venda. Tente novamente.',
        severity: 'error'
      });
    }
  };

  const handleValorChange = (corretorId, valor) => {
    // Remove tudo exceto números
    const apenasNumeros = valor.replace(/\D/g, '');
    // Converte para número e divide por 100 para ter centavos
    const valorNumerico = apenasNumeros ? parseFloat(apenasNumeros) / 100 : 0;
    
    // Atualiza o valor temporário para exibição imediata
    setValoresTemporarios(prev => ({
      ...prev,
      [corretorId]: valorNumerico
    }));
    setTemAlteracoesPendentes(true);
  };

  // Handle Enter key - just move to next field, don't save
  const handleValorKeyDown = (e, corretorId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Move to next field (agenciamento)
      const nextInput = e.target.closest('tr')?.querySelector('input[placeholder="0"]');
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const handleAgenciamentoChange = (corretorId, valor) => {
    // Remove caracteres não numéricos
    const apenasNumeros = valor.replace(/\D/g, '');
    // Converte para número inteiro (não divide por 100, é quantidade)
    const valorNumerico = apenasNumeros ? parseInt(apenasNumeros, 10) : 0;
    
    // Atualiza o valor temporário para exibição imediata
    setAgenciamentosTemporarios(prev => ({
      ...prev,
      [corretorId]: valorNumerico
    }));
    setTemAlteracoesPendentes(true);
  };

  // Handle Enter key - just move to next field, don't save
  const handleAgenciamentoKeyDown = (e, corretorId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Move to next field (numero contratos)
      const nextInput = e.target.closest('tr')?.querySelectorAll('input[placeholder="0"]')[1];
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const handleNumeroContratosChange = (corretorId, valor) => {
    // Remove caracteres não numéricos
    const apenasNumeros = valor.replace(/\D/g, '');
    // Converte para número inteiro (não divide por 100, é quantidade)
    const valorNumerico = apenasNumeros ? parseInt(apenasNumeros, 10) : 0;
    
    // Atualiza o valor temporário para exibição imediata
    setNumeroContratosTemporarios(prev => ({
      ...prev,
      [corretorId]: valorNumerico
    }));
    setTemAlteracoesPendentes(true);
  };

  const handlePontuacaoMetaChange = (corretorId, valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    const valorNumerico = apenasNumeros ? parseInt(apenasNumeros, 10) : 0;

    setPontuacaoMetaTemporarios(prev => ({
      ...prev,
      [corretorId]: valorNumerico
    }));
    setTemAlteracoesPendentes(true);
  };

  // Handle Enter key - just move to next row or do nothing
  const handleNumeroContratosKeyDown = (e, corretorId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Could move to next row if needed, but for now just prevent default
    }
  };

  const handleToggleAtivo = async (corretorId, ativo) => {
    try {
      await atualizarStatusAtivo(corretorId, !ativo);
    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar status. Tente novamente.',
        severity: 'error'
      });
    }
  };

  // Save all pending changes at once
  const handleSalvarTodasAlteracoes = async () => {
    if (!temAlteracoesPendentes) return;
    
    setSalvando(true);
    try {
      // Collect all changes
      const vendasParaSalvar = [];
      const corretoresComAlteracoes = new Set([
        ...Object.keys(valoresTemporarios),
        ...Object.keys(agenciamentosTemporarios),
        ...Object.keys(numeroContratosTemporarios),
        ...Object.keys(pontuacaoMetaTemporarios)
      ]);
      
      corretoresComAlteracoes.forEach(corretorId => {
        const corretor = corretores.find(c => c.id === corretorId);
        if (!corretor) return;
        
        const valorNumerico = valoresTemporarios[corretorId] !== undefined 
          ? valoresTemporarios[corretorId] 
          : getVendaValor(corretor, anoSelecionado, mesSelecionado);
        
        const agenciamentoNumerico = agenciamentosTemporarios[corretorId] !== undefined
          ? parseInt(agenciamentosTemporarios[corretorId], 10)
          : getAgenciamento(corretor, anoSelecionado, mesSelecionado);
        
        const numeroContratosNumerico = numeroContratosTemporarios[corretorId] !== undefined
          ? parseInt(numeroContratosTemporarios[corretorId], 10)
          : getNumeroContratos(corretor, anoSelecionado, mesSelecionado);

        const pontuacaoMetaNumerico = pontuacaoMetaTemporarios[corretorId] !== undefined
          ? parseInt(pontuacaoMetaTemporarios[corretorId], 10)
          : getPontuacaoMeta(corretor, anoSelecionado, mesSelecionado);
        
        vendasParaSalvar.push({
          corretorId: corretorId,
          ano: anoSelecionado,
          mes: mesSelecionado,
          valor: valorNumerico,
          pontuacaoMeta: pontuacaoMetaNumerico,
          agenciamento: agenciamentoNumerico,
          numeroContratos: numeroContratosNumerico,
        });
      });
      
      if (vendasParaSalvar.length > 0) {
        await atualizarVendasBatch(vendasParaSalvar);
        
        // Clear temporary values
        setValoresTemporarios({});
        setAgenciamentosTemporarios({});
        setNumeroContratosTemporarios({});
        setPontuacaoMetaTemporarios({});
        setTemAlteracoesPendentes(false);
        
        setSnackbar({
          open: true,
          message: `${vendasParaSalvar.length} alteração(ões) salva(s) com sucesso!`,
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error saving all changes:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar alterações. Tente novamente.',
        severity: 'error'
      });
    } finally {
      setSalvando(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h5" sx={{ fontWeight: 'bold'  }}>
            VGV {mesSelecionado} {anoSelecionado}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Ano</InputLabel>
            <Select
              value={anoSelecionado}
              label="Ano"
              onChange={(e) => handleAnoChange(e.target.value)}
            >
              {anos.map((ano) => (
                <MenuItem key={ano} value={ano}>
                  {ano}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Mês</InputLabel>
            <Select
              value={mesSelecionado}
              label="Mês"
              onChange={(e) => handleMesChange(e.target.value)}
            >
              {meses.map((mes) => (
                <MenuItem key={mes} value={mes}>
                  {mes}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          {temAlteracoesPendentes && (
            <Button
              variant="contained"
              color="primary"
              startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSalvarTodasAlteracoes}
              disabled={salvando}
              sx={{
                bgcolor: 'rgb(255, 136, 0)',
                color: '#ffffff',
                fontWeight: 'bold',
                letterSpacing: '0.08em',
                '&:hover': {
                  bgcolor: 'rgb(255, 136, 0)',
                  color: '#ffffff',
                },
                '&:disabled': {
                  bgcolor: 'rgb(255, 136, 0)',
                  color: '#ffffff',
                }
              }}
            >
              {salvando ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Adicionar Corretor
          </Button>
        </Box>
      </Box>

      {/* KPI Cards de Resumo Mensal */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 3,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}
            >
              VGV Total do Mês
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#D2B35C', mt: 0.5, fontSize: '1.25rem' }}>
              {formatarMoeda(totalVgvMes)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 3,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}
            >
              Total Agenciamentos
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff', mt: 0.5, fontSize: '1.25rem' }}>
              {totalAgenciamentosMes}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 3,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}
            >
              Total Contratos
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff', mt: 0.5, fontSize: '1.25rem' }}>
              {totalContratosMes}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 3,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}
            >
              Corretores c/ Vendas
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff', mt: 0.5, fontSize: '1.25rem' }}>
              {corretoresComVendaEfetiva} / {corretoresComVendas.length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">Foto</TableCell>
              <TableCell align="center">Posição</TableCell>
              <TableCell align="center">Nome do Corretor</TableCell>
              <TableCell align="center" >Valor Vendido</TableCell>
              <TableCell align="center" >Agenciamentos</TableCell>
              <TableCell align="center" >Nº Contratos</TableCell>
              <TableCell align="center" >Pontuação Meta</TableCell>
              <TableCell align="center">Status Ativo</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {corretoresComVendas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhum corretor com vendas neste mês. Selecione um corretor acima para adicionar.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              corretoresComVendas.map((corretor, index) => (
                <TableRow key={corretor.id} hover>
                  <TableCell>
                    <Avatar 
                      src={corretor.foto || undefined} 
                      alt={corretor.nome} 
                      sx={{ width: 40, height: 40 }}
                    >
                      {!corretor.foto && corretor.nome 
                        ? corretor.nome.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                        : ''}
                    </Avatar>
                  </TableCell>
                  <TableCell>{isCorretorSemPontuacao(corretor) ? '-' : `${index + 1}º`}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">{corretor.nome}</Typography>
                      {isCorretorSemPontuacao(corretor) && (
                        <Chip
                          label="Sem Pontuação"
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                            bgcolor: 'rgba(255, 255, 255, 0.05)',
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right" >
                    <TextField
                      type="text"
                      size="small"
                      value={formatarValorMoeda(
                        valoresTemporarios[corretor.id] !== undefined 
                          ? valoresTemporarios[corretor.id] 
                          : corretor.vgv
                      )}
                      onChange={(e) => handleValorChange(corretor.id, e.target.value)}
                      onKeyDown={(e) => handleValorKeyDown(e, corretor.id)}
                      sx={{ width: '100%', maxWidth: 300 }}
                      placeholder="R$ 0,00"
                    />
                  </TableCell>
                  <TableCell align="right" >
                    <TextField
                      type="text"
                      size="small"
                      value={
                        agenciamentosTemporarios[corretor.id] !== undefined 
                          ? agenciamentosTemporarios[corretor.id].toString()
                          : (corretor.agenciamento || 0).toString()
                      }
                      onChange={(e) => handleAgenciamentoChange(corretor.id, e.target.value)}
                      onKeyDown={(e) => handleAgenciamentoKeyDown(e, corretor.id)}
                      sx={{ width: '100%', maxWidth: 120 }}
                      placeholder="0"
                    />
                  </TableCell>
                  <TableCell align="right" >
                    <TextField
                      type="text"
                      size="small"
                      value={
                        numeroContratosTemporarios[corretor.id] !== undefined 
                          ? numeroContratosTemporarios[corretor.id].toString()
                          : (corretor.numeroContratos || 0).toString()
                      }
                      onChange={(e) => handleNumeroContratosChange(corretor.id, e.target.value)}
                      onKeyDown={(e) => handleNumeroContratosKeyDown(e, corretor.id)}
                      sx={{ width: '100%', maxWidth: 120 }}
                      placeholder="0"
                    />
                  </TableCell>
                  <TableCell align="right" >
                    <TextField
                      type="text"
                      size="small"
                      value={
                        pontuacaoMetaTemporarios[corretor.id] !== undefined
                          ? pontuacaoMetaTemporarios[corretor.id].toString()
                          : (corretor.pontuacaoMeta || 0).toString()
                      }
                      onChange={(e) => handlePontuacaoMetaChange(corretor.id, e.target.value)}
                      sx={{ width: '100%', maxWidth: 140 }}
                      placeholder="0"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={corretor.ativo ? 'Sim' : 'Não'}
                      color={corretor.ativo ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={corretor.ativo}
                          onChange={() => handleToggleAtivo(corretor.id, corretor.ativo)}
                          size="small"
                        />
                      }
                      label=""
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para Adicionar Corretor */}
      <Dialog open={openDialog} onClose={() => {
        setOpenDialog(false);
        setCorretorSelecionado('');
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Corretor ao VGV {mesSelecionado} {anoSelecionado}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Selecione o Corretor</InputLabel>
              <Select
                value={corretorSelecionado}
                label="Selecione o Corretor"
                onChange={(e) => setCorretorSelecionado(e.target.value)}
              >
                {corretoresDisponiveis.length === 0 ? (
                  <MenuItem value="" disabled>
                    Todos os corretores ativos já estão na lista
                  </MenuItem>
                ) : (
                  corretoresDisponiveis.map((corretor) => (
                    <MenuItem key={corretor.id} value={corretor.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar 
                          src={corretor.foto || undefined} 
                          alt={corretor.nome} 
                          sx={{ width: 32, height: 32 }}
                        >
                          {!corretor.foto && corretor.nome 
                            ? corretor.nome.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                            : ''}
                        </Avatar>
                        <Box>
                          <Typography variant="body1">{corretor.nome}</Typography>
                          {corretor.email && (
                            <Typography variant="caption" color="text.secondary">
                              {corretor.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            {corretoresDisponiveis.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Todos os corretores ativos já possuem vendas cadastradas para este mês.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDialog(false);
            setCorretorSelecionado('');
          }}>
            Cancelar
          </Button>
          <Button
            onClick={handleAdicionarCorretor}
            variant="contained"
            disabled={!corretorSelecionado}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          icon={snackbar.severity === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
          sx={{
            bgcolor: snackbar.severity === 'success' ? '#4caf50' : '#f44336',
            color: '#ffffff',
            fontWeight: 500,
            fontSize: '0.95rem',
            '& .MuiAlert-icon': {
              color: '#ffffff',
              fontSize: '1.5rem',
            },
            '& .MuiAlert-action': {
              color: '#ffffff',
            },
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: 2,
            minWidth: 320,
            alignItems: 'center',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default VendasMes;

